import { Card, Icon } from '@iwoca/orion';
import styles from './NewOutstandingView.module.css';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { useQuery } from '@tanstack/react-query';
import {
    getMaxMonthlyInterestRate,
    getTopUpStatus
} from './NewOutstandingView.helpers';
import InternationalisedName from '../dashboardComponents/InternationalisedName';
import { Rate } from '../../../myAccount/ui/rate';
import ProductType from '../../../myAccount/ui/productType/ProductType';
import OverviewPanel from './OverviewPanel/OverviewPanel';
import { Tab } from '@headlessui/react';
import { FAQs } from './FAQs/FAQs';
import { RepaymentsPanel } from './RepaymentsPanel/RepaymentsPanel';
import { RootState } from '@iwProvider/configureStore';
import * as selectors from '../accountsSelector';
import { TProductType } from '../../dashboard/Dashboard.types';
import { connect } from 'react-redux';
import { useEffect, useState } from 'react';
import { tracking } from '@iwoca/frontend-tracking-library';
import RequestMoreFundsPanel from './RequestMoreFundsPanel/RequestMoreFundsPanel';
import TopUpStatusAlertBoxes from './TopUpStatusAlertBoxes/TopUpStatusAlertBoxes';
import DrawdownPanel from './DrawdownPanel/DrawdownPanel';
import { MoreFundsTab } from './MoreFundsTab/MoreFundsTab';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import loanCostMessages from '../dashboardComponents/loanCost/LoanCost.messages';
import { FormattedMessage } from 'react-intl';
import { isMobileWidth } from '../../../iwMedia/BreakpointHelper';
import { InformationBanners } from './InformationBanners/InformationBanners';
import { Divider, Text } from '@ui';

type OwnProps = {
    productType: TProductType;
    accountId: string;
};

const mapStateToProps = (state: RootState, { productType }: OwnProps) => {
    return {
        loanDuration: selectors.getLoanDurationByMonth(state, productType),
        topUpStatus: getTopUpStatus(state, productType)
    };
};
type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const NewOutstandingView = ({
    productType,
    accountId,
    loanDuration,
    topUpStatus
}: OwnProps & PropsFromRedux) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const isTopUpApproved = topUpStatus === 'TOPUP_APPROVED';
    const isPreApproved = topUpStatus === 'PRE_APPROVED';
    const showDrawdownPanel = isTopUpApproved;
    const isMoreFundsTabSelected = selectedIndex === 1;
    const hideFAQs =
        isMoreFundsTabSelected && (isPreApproved || isTopUpApproved);

    useEffect(() => {
        tracking.ampli.dashboardViewed({
            account_id: accountId,
            // @ts-expect-error
            product_type: productType,
            dashboard_state: 'ongoing'
        });
    }, [accountId, productType]);

    return (
        // pb-[125px] ensures the mobile menu doesn't block any content
        <div className="mx-auto mt-l max-w-3xl px-2xs pb-[125px] md:pb-xl">
            {(!isMobileWidth() || !isMoreFundsTabSelected) && (
                <TopUpStatusAlertBoxes
                    productType={productType}
                    accountId={accountId}
                />
            )}

            <Card className="mt-s">
                <LoanInformationHeader accountId={accountId} />

                <Tab.Group
                    selectedIndex={selectedIndex}
                    onChange={setSelectedIndex}
                >
                    <Tab.List className={styles.tabsList}>
                        <Tab className={styles.tab}>
                            <Icon icon="houseFilled" className={styles.icon} />{' '}
                            Overview
                        </Tab>

                        <MoreFundsTab
                            accountId={accountId}
                            topUpStatus={topUpStatus}
                        />

                        <Tab className={styles.tab}>
                            <Icon icon="sync" className={styles.icon} />{' '}
                            Repayments
                        </Tab>
                    </Tab.List>

                    <div className="w-full pt-l">
                        <Tab.Panels>
                            <Tab.Panel>
                                <OverviewPanel accountId={accountId} />
                            </Tab.Panel>

                            {showDrawdownPanel ? (
                                <Tab.Panel>
                                    <DrawdownPanel />
                                </Tab.Panel>
                            ) : (
                                <Tab.Panel>
                                    <RequestMoreFundsPanel
                                        accountId={accountId}
                                        productType={productType}
                                    />
                                </Tab.Panel>
                            )}

                            <Tab.Panel>
                                <RepaymentsPanel accountId={accountId} />
                            </Tab.Panel>
                        </Tab.Panels>

                        <Divider />

                        <InformationBanners />

                        {!hideFAQs && (
                            <FAQs
                                isTopUpApproved={isTopUpApproved}
                                loanDuration={loanDuration}
                            />
                        )}
                    </div>
                </Tab.Group>
            </Card>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(NewOutstandingView);

const LoanInformationHeader = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();

    const {
        data: getAccountsResponse,
        isLoading,
        isError
    } = useQuery(getAccountsQueryConfig(stateKey));

    // TODO: Improve loading skeleton
    if (isLoading || isError) return null;

    const account = getAccountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const productType = account.product_type;

    const { amount, unit } = account.duration!;
    const durationInMonths =
        unit === 'days' ? selectors.getMonthsfromDays(amount) : amount;
    const isLongerLoan = durationInMonths > 24;

    const interestRate = getMaxMonthlyInterestRate(account);

    return (
        <>
            <Text className="font-medium">
                Hello, <InternationalisedName />
            </Text>
            <Text>
                You're looking at your{' '}
                <ProductType
                    productType={productType!}
                    isDynamic={isLongerLoan}
                />
                <FormattedMessage
                    {...loanCostMessages.youllPay}
                    values={{
                        approvalInterestRate: <Rate value={interestRate!} />
                    }}
                />
            </Text>
        </>
    );
};
